function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
}

function lerp(a, b, t) {
    return a + (b - a) * t;
}

function parseSeconds(value, fallback) {
    if (typeof value !== "string") {
        return fallback;
    }

    const normalizedValue = value.trim().replace(",", ".");
    const seconds = parseFloat(normalizedValue);

    return Number.isFinite(seconds) ? seconds : fallback;
}

function initVideoScale(options = {}) {
    const {
        sectionSelector = ".video-scale",
        containerSelector = ".container, .site-container, .page-container",
        marginRem = 10,
        easing = (t) => t,
        mobileBreakpoint = 991.98,
    } = options;

    const sections = Array.from(document.querySelectorAll(sectionSelector));
    if (!sections.length) return;

    const isMobile = () => window.innerWidth <= mobileBreakpoint;

    function remToPx(rem) {
        const fs = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        return rem * fs;
    }

    function ensureAutoplay(video) {
        if (!video || video.dataset.autoplayInitialized === "true") {
            return;
        }

        video.dataset.autoplayInitialized = "true";

        video.muted = true;
        video.playsInline = true;
        video.autoplay = true;

        const tryPlay = () => {
            const p = video.play();

            if (p && typeof p.catch === "function") {
                p.catch(() => {});
            }
        };

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        tryPlay();
                    } else {
                        video.pause();
                    }
                });
            },
            {
                threshold: 0.35
            }
        );

        observer.observe(video);

        const unlock = () => {
            if (!video.paused) {
                return;
            }

            const rect = video.getBoundingClientRect();
            const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

            if (isVisible) {
                tryPlay();
            }

            window.removeEventListener("scroll", unlock);
            window.removeEventListener("click", unlock);
            window.removeEventListener("touchstart", unlock);
            window.removeEventListener("keydown", unlock);
        };

        window.addEventListener("scroll", unlock, { once: true, passive: true });
        window.addEventListener("click", unlock, { once: true });
        window.addEventListener("touchstart", unlock, { once: true, passive: true });
        window.addEventListener("keydown", unlock, { once: true });
    }

    function setupSegmentLoop(video) {
        if (!video || video.dataset.segmentLoopInitialized === "true") {
            return;
        }

        const loopEnabled = video.dataset.segmentLoopEnabled === "true";
        if (!loopEnabled) return;

        let loopStart = parseSeconds(video.dataset.loopStart || "15", 15);
        let loopEnd = parseSeconds(video.dataset.loopEnd || "30", 30);

        if (!Number.isFinite(loopStart) || loopStart < 0) {
            loopStart = 15;
        }

        if (!Number.isFinite(loopEnd) || loopEnd <= 0) {
            loopEnd = 30;
        }

        video.dataset.segmentLoopInitialized = "true";
        video.loop = false;

        let firstRunCompleted = false;
        let resolvedLoopEnd = loopEnd;

        const resolveLoopBounds = () => {
            const duration = video.duration;

            if (!Number.isFinite(duration) || duration <= 0) return;

            resolvedLoopEnd = Math.min(loopEnd, duration);

            if (loopStart >= resolvedLoopEnd) {
                loopStart = Math.max(0, resolvedLoopEnd - 0.25);
            }
        };

        video.addEventListener("loadedmetadata", resolveLoopBounds);
        video.addEventListener("durationchange", resolveLoopBounds);

        resolveLoopBounds();

        video.addEventListener("ended", () => {
            firstRunCompleted = true;
            video.currentTime = loopStart;
            video.play().catch(() => {});
        });

        video.addEventListener("timeupdate", () => {
            if (!firstRunCompleted) return;
            if (!Number.isFinite(video.duration)) return;

            if (video.currentTime >= resolvedLoopEnd) {
                video.currentTime = loopStart;
                video.play().catch(() => {});
            }
        });
    }

    function computeTargets(section) {
        const video = section.querySelector("video");
        if (!video) return null;

        ensureAutoplay(video);
        setupSegmentLoop(video);

        const videoWrapper = video.closest(".video");

        const container =
            section.closest(containerSelector) ||
            document.querySelector(containerSelector);

        const viewportW = window.innerWidth;
        const marginPx = remToPx(marginRem);

        let endWidth = Math.max(0, viewportW - 2 * marginPx);

        if (container) {
            const containerW = container.getBoundingClientRect().width;
            endWidth = Math.min(endWidth, containerW);
        }

        const startWidth = viewportW;
        const endScale = startWidth > 0 ? endWidth / startWidth : 1;

        return { video, videoWrapper, endScale };
    }

    function getProgress(section) {
        const rect = section.getBoundingClientRect();
        const sectionTopInDoc = window.scrollY + rect.top;
        const sectionHeight = rect.height;
        const viewportH = window.innerHeight;

        const scrollable = Math.max(1, sectionHeight - viewportH);
        const scrolled = window.scrollY - sectionTopInDoc;

        return clamp(scrolled / scrollable, 0, 1);
    }

    function applyMobileState() {
        sections.forEach((section) => {
            const video = section.querySelector("video");
            if (!video) return;

            ensureAutoplay(video);
            setupSegmentLoop(video);

            const videoWrapper = video.closest(".video");

            if (videoWrapper) {
                videoWrapper.style.setProperty("--video-scale-factor", "1");
            }

            section.style.setProperty("--headline-bottom", "0px");
            section.style.setProperty("--headline-opacity", "1");
        });
    }

    const state = new Map();

    function recalcAll() {
        if (isMobile()) {
            applyMobileState();
            return;
        }

        sections.forEach((section) => {
            const data = computeTargets(section);

            if (data) {
                state.set(section, data);
            }
        });
    }

    let ticking = false;

    function update() {
        ticking = false;

        if (isMobile()) {
            applyMobileState();
            return;
        }

        sections.forEach((section) => {
            const data = state.get(section);
            if (!data) return;

            const t = easing(getProgress(section));
            const scale = lerp(1, data.endScale, t);

            if (data.videoWrapper) {
                data.videoWrapper.style.setProperty("--video-scale-factor", String(scale));
            }

            const endBottomPx = remToPx(60);
            const bottomPx = lerp(0, endBottomPx, t);
            const opacity = t > 0 ? 1 : 0;

            section.style.setProperty("--headline-bottom", `${bottomPx}px`);
            section.style.setProperty("--headline-opacity", String(opacity));
        });
    }

    function onScroll() {
        if (isMobile()) return;

        if (!ticking) {
            ticking = true;
            requestAnimationFrame(update);
        }
    }

    function onResize() {
        recalcAll();
        update();
    }

    const io = new IntersectionObserver(
        (entries) => {
            if (isMobile()) return;

            if (entries.some((e) => e.isIntersecting)) {
                onScroll();
            }
        },
        {
            threshold: 0,
            rootMargin: "200px 0px 200px 0px"
        }
    );

    recalcAll();

    sections.forEach((section) => {
        io.observe(section);
    });

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    update();

    return () => {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onResize);
        io.disconnect();
    };
}

initVideoScale({
    containerSelector: ".container, .neos-contentcollection, .content-container",
    marginRem: 10,
    mobileBreakpoint: 991.98,
});
